import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Modal, Input, Button, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ColumnGroup, Column, Card } from '@cards/Card';
import { usePageActions } from '@hooks';
import { selectOrganisationFromList } from '@redux/organisations/reducers';

import { loadOrgConfig, editOrgConfig, createOrgConfig } from '@integrations/redux/actions';
import { selectIsSubmitting, selectOrgConfig } from '@integrations/redux/reducers';
import { fetchOrganisations } from '@redux/organisations/actions';
import './style.less';

const confirmExit = (t, history) => {
  Modal.confirm({
    title: t('organisations:EditOrganisationDetails.exitTitle'),
    content: t('organisations:EditOrganisationDetails.exitContent'),
    okText: t('organisations:EditOrganisationDetails.okText'),
    cancelText: t('organisations:EditOrganisationDetails.cancelText'),
    onOk: history.goBack,
  });
};

const detailsLayout = {
  labelCol: {
    span: 6,
  },
  layout: 'vertical',
  wrapperCol: {
    span: 18,
  },
};

function OrganisationConfig({
  form: { getFieldDecorator, validateFieldsAndScroll },
  match: {
    params: { id: organisationId },
  },
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [hasLoaded, setHasLoaded] = useState(false);

  const isSubmitting = useSelector(selectIsSubmitting);
  const [, organisation] = useSelector(selectOrganisationFromList(organisationId));

  const [, orgConfig] = useSelector(
    selectOrgConfig('mixpanel', organisation || { slug: 'unknown' }),
  );

  let dashboardURL = '';
  if (orgConfig && orgConfig.data.dashboard_url) {
    dashboardURL = orgConfig.data.dashboard_url;
  }

  const { setTitle } = usePageActions({
    title: '',
    actions: [],
    showClose: false,
    onClose: () => confirmExit(t, history),
  });

  useEffect(() => {
    const name = (organisation && organisation.name) || '';
    setTitle(name);
    document.title = name;
  }, [organisation, setTitle]);

  useEffect(() => {
    if (!organisation) {
      dispatch(fetchOrganisations());
    }
  }, [organisationId, organisation, dispatch]);

  useEffect(() => {
    if (hasLoaded || !organisation) {
      return;
    }
    dispatch(loadOrgConfig('mixpanel', organisation.slug));
    setHasLoaded(true);
  }, [dispatch, hasLoaded, setHasLoaded, organisation]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      validateFieldsAndScroll((err, values) => {
        if (!err) {
          const data = {
            dashboard_url: values.dashboardURL,
          };
          if (orgConfig) {
            dispatch(editOrgConfig('mixpanel', values.isEnabled, data, organisation.slug));
          } else {
            dispatch(createOrgConfig('mixpanel', data, organisation.slug));
          }
        }
      });
    },
    [dispatch, validateFieldsAndScroll, organisation, orgConfig],
  );

  return (
    <ColumnGroup className="edit-organisation-branding-details">
      <Column>
        <Card
          className="organisation-details-form-card"
          title={t('mixpanel:organisationConfig.title')}
        >
          <Form {...detailsLayout} onSubmit={handleSubmit}>
            <Form.Item
              className="details-item"
              label={t('mixpanel:organisationConfig.dashboardURLLabel')}
              help={t('mixpanel:organisationConfig.dashboardURLHelpText')}
              defaultValue={orgConfig && orgConfig.data && orgConfig.data.dashboard_url}
            >
              {getFieldDecorator('dashboardURL', {
                rules: [
                  {
                    required: true,
                    pattern: /https:\/\/(eu.)?mixpanel.com\/(.*)/,
                    message: t('mixpanel:organisationConfig.dashboardURLRequired'),
                  },
                ],
                initialValue: orgConfig && orgConfig.data && orgConfig.data.dashboard_url,
              })(<Input type="text" placeholder="Dashboard URL" allowClear={true} />)}
            </Form.Item>
            {orgConfig ? (
              <Form.Item
                className="details-item"
                label={t('mixpanel:organisationConfig.isEnabled')}
                help={t('mixpanel:organisationConfig.isEnabledHelpText')}
              >
                {getFieldDecorator('isEnabled', {
                  rules: [],
                  initialValue: true,
                })(<Checkbox style={{ marginLeft: '0' }} defaultChecked={orgConfig.isEnabled} />)}
              </Form.Item>
            ) : null}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                style={{ marginRight: '10px' }}
                loading={isSubmitting}
                disabled={!hasLoaded}
              >
                {t('organisations:Branding.details.edit.save')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
        {dashboardURL ? (
          <>
            <Card
              className="organisation-details-form-card"
              title={t('mixpanel:organisationConfig.viewTitle')}
            >
              <p>{t('mixpanel:organisationConfig.viewDescription')}</p>
            </Card>
            <iframe
              src={dashboardURL}
              style={{ minHeight: '1080px' }}
              title={t('mixpanel:organisationConfig.viewTitle')}
            ></iframe>
          </>
        ) : null}
      </Column>
    </ColumnGroup>
  );
}

const WrappedOrganisationConfig = Form.create()(OrganisationConfig);

export default WrappedOrganisationConfig;
